import {
  Cartesian2,
  Cartesian3,
  CloudCollection,
  DeveloperError,
  Math as CesiumMath,
} from "@cesium/engine";
import DitapViewer from "../basic/DitapViewer";

/**
 * 구름을 생성하고 표시하는 클래스.</br>
 * Class for creating and displaying clouds.
 *
 * @alias Cloud
 * @constructor
 * @param {object} cloudOptions 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {DitapViewer} cloudOptions.viewer 사용할 Ditap Viewer 인스턴스.</br>The DitapViewer instance to use.
 * @param {Number} cloudOptions.longitude 구름의 중심 경도.</br>Longitude of the cloud center.
 * @param {Number} cloudOptions.latitude 구름의 중심 위도.</br>Latitude of the cloud center.
 * @param {Number} cloudOptions.height 구름의 높이(m).(기본값: 1500)</br>Height of the cloud (in meters).(default: 1500)
 * @param {Number} cloudOptions.amount 구름의 양을 나타내는 값(m).(기본값:5, 최소값: 0, 최대값: 10).</br> Amount of clouds(in meters).(default: 5, minimum:0, maximum: 10).
 * @param {Number} cloudOptions.radius 구름이 생성될 범위를 나타내는 반지름 값(m).(기본값: 100000)</br>Radius value indicating the range in which clouds will be generated(in meters).(default: 100000)
 * @param {Number} cloudOptions.heightRange 구름의 높이 범위(m).(기본값: 250)</br>Height range of the clouds(in meters).(default: 250)
 *
 * @example
 * // 샌드캐슬 예제 'DiTAP - Cloud' 참조
 * const viewer = new Ditap.DitapViewer("ditapContainer", {
 *   fullscreenButton: true,
 * });
 *
 * // 구름 효과 인스턴스 생성
 * const cloud = new Ditap.Cloud({
 *   viewer: viewer,
 *   longitude: 126.978275264,
 *   latitude: 37.566642192,
 *   radius: 10000,
 *   amount: 5,
 *   height: 1500,
 *   heightRange: 250,
 * });
 *
 * // 카메라 이동
 * viewer.camera.setView({
 *   destination: Ditap.Cartesian3.fromDegrees(126.978275264, 37.566642192, 200),
 *   orientation: {
 *     heading: Ditap.Math.toRadians(0),
 *     pitch: Ditap.Math.toRadians(-10),
 *     roll: 0,
 *   },
 * });
 *
 * // 구름 효과 시작
 * cloud.start()
 */
class Cloud {
  constructor(cloudOptions) {
    if (cloudOptions.viewer instanceof DitapViewer) {
      /**
       * 사용할 Ditap Viewer 인스턴스.</br>
       * The DitapViewer instance to use.
       * @type {DitapViewer}
       *
       * */
      this.viewer = cloudOptions.viewer;
    }

    const availAbleOptions = this.checkCloudOptions(cloudOptions);

    /**
     * 구름의 중심 경도.</br>Longitude of the cloud center.
     *
     * @type {Number}
     */
    this.longitude = availAbleOptions.longitude;
    /**
     * 구름의 중심 위도.</br>Latitude of the cloud center.
     *
     * @type {Number}
     */
    this.latitude = availAbleOptions.latitude;
    /**
     * 구름이 생성될 범위를 나타내는 반지름 값(m).(기본값: 100000)</br>
     * Radius value indicating the range in which clouds will be generated(in meters).(default: 100000)
     *
     * @type {Number}
     */
    this.radius = availAbleOptions.radius;
    /**
     * 구름의 높이 범위(m).(기본값: 250)
     * </br>Height range of the clouds(in meters).(default: 250)
     *
     * @type {Number}
     */
    this.heightRange = availAbleOptions.heightRange; // 구름 높이 범위

    this.amount = availAbleOptions.amount;
    this.height = availAbleOptions.height;
    this._isStart = false;
  }

  /**
   * 구름의 양을 나타내는 값(m).(기본값:5, 최소값: 0, 최대값: 10).</br>
   * Amount of clouds(in meters).(default: 5, minimum:0, maximum: 10).
   *
   * @type {Number}
   */
  get amount() {
    return this._amount;
  }

  set amount(value) {
    this._amount = value;
    if (this._isStart) {
      this.start();
    }
  }

  /**
   * 구름의 높이(m).(기본값: 1500)</br>
   * Height of the cloud (in meters).(default: 1500)
   *
   * @type {Number}
   */
  get height() {
    return this._height;
  }

  set height(value) {
    this._height = value;
    if (this._isStart) {
      this.start();
    }
  }

  /**
   * 사용자가 설정한 구름 옵션을 유효성 검사 및 기본값 설정.</br>
   * Validates user-defined cloud options and sets default values.
   *
   * @param {object} cloudOptions 다음과 같은 속성을 가진 객체.</br> Object with the following properties: instance to use.
   * @param {Number} cloudOptions.longitude 구름의 중심 경도.</br>Longitude of the cloud center.
   * @param {Number} cloudOptions.latitude 구름의 중심 위도.</br>Latitude of the cloud center.
   * @param {Number} cloudOptions.height 구름의 높이(m).(기본값: 1500)</br>Height of the cloud (in meters).(default: 1500)
   * @param {Number} cloudOptions.amount 구름의 양을 나타내는 값(m).(기본값:5, 최소값: 0, 최대값: 10).</br> Amount of clouds(in meters).(default: 5, minimum:0, maximum: 10).
   * @param {Number} cloudOptions.radius 구름이 생성될 범위를 나타내는 반지름 값(m).(기본값: 100000)</br>Radius value indicating the range in which clouds will be generated(in meters).(default: 100000)
   * @param {Number} cloudOptions.heightRange 구름의 높이 범위(m).(기본값: 250)</br>Height range of the clouds(in meters).(default: 250)
   * @returns {Number} cloudOptions 유효성 검사가 완료된 구름 옵션.</br> Cloud options after validation.
   */
  checkCloudOptions(cloudOptions) {
    let amount = cloudOptions.amount;
    let radius = cloudOptions.radius;
    let heightRange = cloudOptions.heightRange;
    let height = cloudOptions.height;
    const longitude = cloudOptions.longitude;
    const latitude = cloudOptions.latitude;

    if (typeof amount !== "number") {
      amount = 5;
    }
    if (amount > 10) {
      amount = 10;
    }
    if (amount < 0) {
      amount = 0;
    }
    if (typeof longitude !== "number") {
      throw new DeveloperError("longitude invalid");
    }
    if (typeof latitude !== "number") {
      throw new DeveloperError("latitude invalid");
    }
    if (typeof radius !== "number") {
      radius = 100000;
    }
    if (radius < 0) {
      radius = 0;
    }
    if (typeof heightRange !== "number") {
      heightRange = 250;
    }
    if (typeof height !== "number") {
      height = 1500;
    }

    cloudOptions.amount = amount;
    cloudOptions.radius = radius;
    cloudOptions.heightRange = heightRange;
    cloudOptions.height = height;

    return cloudOptions;
  }

  /**
   * 구름 효과 시작.</br>
   * Starts the clouds effect.
   */
  start() {
    this.stop();
    this.clouds = this.createClouds();
    this.viewer.scene.primitives.add(this.clouds);
    this._isStart = true;
  }

  /**
   * 구름 효과 중지.</br>
   * Stops the clouds effect.
   */
  stop() {
    this._isStart = false;
    if (this.viewer.scene.primitives.contains(this.clouds) && this.clouds) {
      this.viewer.scene.primitives.remove(this.clouds);
    }
  }

  /**
   * 객체를 파괴하고 관련 리소스를 해제합니다. </br>
   * Destroys the object and releases associated resources.
   */
  destroy() {
    // 구름 효과 중지
    this.stop();

    // 뷰어 연결 해제
    this.viewer = undefined;
  }

  /*
   * 주어진 범위 내에서 무작위로 정수를 생성.</br>
   * Generates a random integer within the given range.
   *
   * @param {Number} maxNum 최대값.</br>Maximum value.
   * @param {Number} minNum 최소값.</br>Minimum value.
   * @returns {Number} 무작위 정수.</br>Random integer.
   */
  getRandomNum(maxNum, minNum) {
    return Math.floor(Math.random() * (maxNum - minNum + 1)) + minNum;
  }

  /*
   * 구름 생성 영역 내에 생성할 구름의 개수를 계산(전운량 계산).</br>
   * Calculates the number of clouds to be generated within the cloud generation area (total cloud amount).
   *
   * @returns {Number} cloudsNum 구름 개수.</br>Number of clouds.
   */
  getCloudNum() {
    // 전운량 조건
    /*
            0 ~ 5 맑음
            6 ~ 8 구름 많음
            9 ~ 10 흐림
        */
    const currentArea = Math.PI * this.radius ** 2;

    const k = this.estimateValue(this.amount);

    /**
     * 1: 1050000 15%
     * 2: 900000 30%
     * 3: 750000
     * 4: 500000
     * 5: 250000 10%
     * 6: 150000
     * 7: 100000
     * 8: 60000
     * 9: 40000
     * 10: 20000
     */
    // 면적당 구름 개수
    return (currentArea / k) * (this.amount / 10);
  }

  estimateValue(x) {
    // 주어진 데이터 포인트
    const dataPoints = {
      1: 1050000,
      2: 900000,
      3: 750000,
      4: 500000,
      5: 250000,
      6: 150000,
      7: 100000,
      8: 60000,
      9: 40000,
      10: 20000,
    };

    // x가 정확히 데이터 포인트에 해당하는 경우 바로 반환
    if (dataPoints[x]) {
      return dataPoints[x];
    }

    // x가 범위 밖인 경우 처리
    if (x <= 1) {
      return dataPoints[1];
    }
    if (x >= 10) {
      return dataPoints[10];
    }

    // x가 소수점을 포함하고 있을 때 선형 보간을 위한 계산
    const lower = Math.floor(x);
    const upper = Math.ceil(x);

    // 선형 보간 계산
    const ratio = (x - lower) / (upper - lower);
    const estimatedValue =
      dataPoints[lower] + ratio * (dataPoints[upper] - dataPoints[lower]);

    return estimatedValue;
  }

  /**
   * 구름 생성 영역 내에 무작위 좌표에 구름을 생성.</br>
   * Generates clouds at random coordinates within the cloud generation area.
   *
   * @returns {CloudCollection} clouds 구름 컬렉션.</br>Cloud collection.
   */
  createClouds() {
    const clouds = new CloudCollection();
    const cloudsNum = this.getCloudNum();

    for (let i = 0; i < cloudsNum; i++) {
      const randomWidth = this.getRandomNum(1600, 500);
      const randomHeight = this.getRandomNum(400, 200);
      const randomSlice = this.getRandomNum(0.9, 0.3);
      const position = this.getRandomPointInCircle();

      clouds.add({
        position: position,
        scale: new Cartesian2(randomWidth, randomHeight),
        maximumSize: new Cartesian3(30, 20, 17),
        slice: randomSlice,
      });
    }

    return clouds;
  }

  /*
   * 구름 생성 영역 내에 무작위 cartetian3값 반환.</br>
   * @returns cartesian 무작위로 생성된 점의 cartesian3 좌표.</br>
   */
  getRandomPointInCircle() {
    // 반지름 내의 무작위 각도 및 거리 생성
    const randomAngle = Math.random() * 2 * Math.PI;
    const randomRadius = Math.sqrt(Math.random()) * this.radius;

    // 무작위 점의 위경도 계산
    const randomLongitude =
      this.longitude +
      CesiumMath.toDegrees(
        (randomRadius /
          (6371000 * Math.cos(CesiumMath.toRadians(this.latitude)))) *
          Math.cos(randomAngle)
      );
    const randomLatitude =
      this.latitude +
      CesiumMath.toDegrees((randomRadius / 6371000) * Math.sin(randomAngle));

    // 무작위 점을 Cartesian3로 변환
    const randomCartesian = Cartesian3.fromDegrees(
      randomLongitude,
      randomLatitude
    );

    const maxHeight = this.height + this.heightRange;
    const minHeight = this.height - this.heightRange;

    // 지구로부터의 고도를 설정
    const altitude = this.getRandomNum(maxHeight, minHeight);

    // 고도를 적용한 Cartesian3 반환
    return Cartesian3.fromElements(
      randomCartesian.x,
      randomCartesian.y,
      randomCartesian.z + altitude
    );
  }
}
export default Cloud;
